import type { Timestamp } from '@marmon/shared/types/common';
import type {
	EntityAlertKey,
	EntitySetting,
	EntitySettingPatch,
	EntitySettingType,
} from '../interfaces/entity-setting.interface';

export function entitySettingDocId(
	entityType: EntitySettingType,
	entityId: string,
): string {
	return `${entityType}_${entityId}`;
}

export function isEntitySystemMuted(
	setting: Pick<EntitySetting, 'muteSystem'> | null | undefined,
): boolean {
	return setting?.muteSystem === true;
}

export function entityAlertSentAt(
	setting: Pick<EntitySetting, 'alerts'> | null | undefined,
	key: EntityAlertKey,
): Timestamp | null {
	const sentAt = setting?.alerts?.[key]?.sentAt;
	if (!sentAt || typeof sentAt.toMillis !== 'function') return null;
	return sentAt;
}

/**
 * Returns the next document for a full write. Fields the patch does not
 * mention stay as they are: muting or unmuting keeps every alert timestamp,
 * and recording one alert does not clear the others or change mute.
 */
export function applyEntitySettingChange(
	current: EntitySetting | null,
	identity: { entityType: EntitySettingType; entityId: string },
	patch: EntitySettingPatch,
	now: Timestamp,
): EntitySetting {
	const alerts: EntitySetting['alerts'] = { ...(current?.alerts ?? {}) };
	if (patch.alert) {
		alerts[patch.alert.key] = {
			...alerts[patch.alert.key],
			sentAt: patch.alert.sentAt,
		};
	}

	return {
		...current,
		id: entitySettingDocId(identity.entityType, identity.entityId),
		entityType: identity.entityType,
		entityId: identity.entityId,
		muteSystem: patch.muteSystem ?? current?.muteSystem ?? false,
		alerts,
		createdAt: current?.createdAt ?? now,
		updatedAt: now,
		deletedAt: current?.deletedAt ?? null,
	};
}
